// src/services/danceStylesService.js
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Charge les styles depuis Firestore.
 * Aucun index composite n'est nécessaire : le tri est fait côté client.
 */
export async function fetchDanceStyles({ includeInactive = false } = {}) {
  const snapshot = await getDocs(collection(db, 'danceStyles'));

  return snapshot.docs
    .map((styleDoc) => ({
      id: styleDoc.id,
      ...styleDoc.data(),
    }))
    .filter((style) => includeInactive || style.active !== false)
    .sort((a, b) => {
      const parentCompare = String(a.parentId || '').localeCompare(String(b.parentId || ''));
      if (parentCompare !== 0) return parentCompare;

      const orderCompare = (Number(a.order) || 0) - (Number(b.order) || 0);
      if (orderCompare !== 0) return orderCompare;

      return String(a.label || a.id).localeCompare(String(b.label || b.id), 'fr');
    });
}

export function normalizeDanceStyleIds(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((id) => typeof id === 'string' && id.trim()).map((id) => id.trim()))];
}

export function getDanceStyleLabel(styles, styleId) {
  return styles.find((style) => style.id === styleId)?.label || styleId || '';
}

export function getDanceStyleGroups(styles) {
  const parents = styles
    .filter((style) => !style.parentId)
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

  return parents.map((parent) => ({
    ...parent,
    children: styles
      .filter((style) => style.parentId === parent.id)
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)),
  }));
}

/**
 * Un filtre sur une catégorie principale correspond :
 * - aux cours contenant directement la catégorie ;
 * - aux cours contenant l'une de ses sous-catégories.
 *
 * Un filtre sur une sous-catégorie correspond uniquement à cette sous-catégorie.
 */
export function courseMatchesDanceStyle(courseStyleIds, selectedStyleId, styles) {
  if (!selectedStyleId) return true;

  const normalizedCourseStyles = normalizeDanceStyleIds(courseStyleIds);
  if (normalizedCourseStyles.includes(selectedStyleId)) return true;

  const selectedStyle = styles.find((style) => style.id === selectedStyleId);
  if (!selectedStyle || selectedStyle.parentId) return false;

  const childIds = styles
    .filter((style) => style.parentId === selectedStyleId)
    .map((style) => style.id);

  return childIds.some((childId) => normalizedCourseStyles.includes(childId));
}
